import React from 'react';
import { connect } from 'react-redux';
// Store
import { Dispatch } from 'redux';
import { debounce } from 'debounce';

import { setMediaSize, setOrientation } from '../../redux/appreadouts/actions';
// Enums
import { DeviceOrientation, MediaSize } from '../../enums';
// Interfaces
import { IRootState } from '../../redux';

export const xLargeWidthMin = 1200;
export const largeWidthMin = 992;
export const mediumWidthMin = 768;
export const smallWidthMin = 576;

interface IResizeHandlerProps {
    children: React.ReactNode;
}

interface IResizeHandlerState {
    mediaQueryList: MediaQueryList;
}

const RESIZE_DEBOUNCE_TIMEOUT_MS = 50;

class _ResizeHandler extends React.Component<ReduxType, IResizeHandlerState> {
    constructor(props: ReduxType) {
        super(props);
        this.state = {
            mediaQueryList: null,
        };

        this.resizeWindow = this.resizeWindow.bind(this);
    }

    componentDidMount() {
        this.resizeWindow();
        window.addEventListener('resize', this.resizeWindow);
        const mediaQueryList = window.matchMedia('(orientation:landscape)');
        mediaQueryList.addEventListener('change', this.updateOrientation);
        this.setState({ mediaQueryList: mediaQueryList });
        this.initiateOrientation();
    }

    componentWillUnmount() {
        this.state.mediaQueryList.removeEventListener('change', this.updateOrientation);
        window.removeEventListener('resize', this.resizeWindow);
    }

    componentDidUpdate(prevProps: ReduxType) {
        if (prevProps.isAuthorized !== this.props.isAuthorized) {
            this.resizeWindow();
        }
    }

    resizeWindow = debounce(() => {
        const mediaSize = getMediaSize();
        this.props.setMediaSize(mediaSize);
    }, RESIZE_DEBOUNCE_TIMEOUT_MS);

    updateOrientation = (event: MediaQueryListEvent) => {
        const isLandscape = event.matches;
        this.props.setOrientation(isLandscape ? DeviceOrientation.landscape : DeviceOrientation.portrait);
    };

    initiateOrientation = () => {
        const isLandscape = window.innerHeight <= window.innerWidth;
        this.props.setOrientation(isLandscape ? DeviceOrientation.landscape : DeviceOrientation.portrait);
    };

    public render() {
        return this.props.children;
    }
}

const getMediaSize = (): MediaSize => {
    if (window.innerWidth < smallWidthMin) {
        return MediaSize.XSMALL;
    } else if (window.innerWidth >= smallWidthMin && window.innerWidth < mediumWidthMin) {
        return MediaSize.SMALL;
    } else if (window.innerWidth >= mediumWidthMin && window.innerWidth < largeWidthMin) {
        return MediaSize.MEDIUM;
    } else if (window.innerWidth >= largeWidthMin && window.innerWidth < xLargeWidthMin) {
        return MediaSize.LARGE;
    } else {
        return MediaSize.XLARGE;
    }
};

const mapStateToProps = ({ user }: IRootState) => {
    return {
        isAuthorized: user.isAuthorized,
    };
};

const mapDispatcherToProps = (dispatch: Dispatch) => {
    return {
        setMediaSize: (size: MediaSize) => dispatch(setMediaSize(size)),
        setOrientation: (orientation: DeviceOrientation) => dispatch(setOrientation(orientation)),
    };
};

type ReduxType = IResizeHandlerProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatcherToProps>;

export const ResizeHandler = connect(mapStateToProps, mapDispatcherToProps)(_ResizeHandler);
